/**
 * Cloudflare Stream video upload utility using tus-js-client
 * Implements Direct Creator Uploads with resumable upload support
 */
import * as tus from 'tus-js-client';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Upload a video to Cloudflare Stream using Direct Creator Upload
 * 
 * @param {File} file - The video file to upload
 * @param {Object} options - Upload options
 * @param {Function} options.onProgress - Progress callback (percentage: number, bytesUploaded: number, bytesTotal: number)
 * @param {Function} options.onSuccess - Success callback (uid: string)
 * @param {Function} options.onError - Error callback (error: Error)
 * @param {Function} options.onUploadCreated - Callback when upload instance is created (upload: tus.Upload) - use for abort
 * @returns {Promise<{uid: string, upload: tus.Upload}>} - The video UID and tus upload instance
 */
export async function uploadVideoToCloudflareStream(file, options = {}) {
  const { onProgress, onSuccess, onError, onUploadCreated } = options;
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Validate file type
  const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'video/x-matroska'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid video format. Supported formats: MP4, MOV, AVI, MPEG, WebM, MKV');
  }

  // Get upload URL from backend
  const response = await fetch(`${API_URL}/api/videos/upload-url`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      max_duration_seconds: 7200, // 2 hours max
      file_size: file.size
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  const { upload_url, uid, expires_at } = await response.json();

  return new Promise((resolve, reject) => {
    // Create tus upload instance
    const upload = new tus.Upload(file, {
      endpoint: upload_url,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      chunkSize: 50 * 1024 * 1024, // 50MB chunks (recommended for reliable connections)
      metadata: {
        filename: file.name,
        filetype: file.type
      },
      onError: (error) => {
        console.error('Cloudflare Stream upload error:', error);
        if (onError) onError(error);
        reject(error);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        // Pass all three values: percentage, bytesUploaded, bytesTotal
        if (onProgress) onProgress(percentage, bytesUploaded, bytesTotal);
      },
      onSuccess: () => {
        console.log('Cloudflare Stream upload complete, UID:', uid);
        if (onSuccess) onSuccess(uid);
        resolve({ uid, upload });
      }
    });

    // Expose the upload instance to the caller for abort functionality
    if (onUploadCreated) {
      onUploadCreated(upload);
    }

    // Check for previous uploads to resume
    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length > 0) {
        // Resume the most recent upload
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      // Start the upload
      upload.start();
    });
  });
}


/**
 * Get the status of an uploaded video
 * 
 * @param {string} uid - The Cloudflare Stream video UID
 * @returns {Promise<Object>} - Video status information
 */
export async function getVideoStatus(uid) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}/api/videos/${uid}/status`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get video status');
  }

  return response.json();
}


/**
 * Get the playback URL for a video
 * 
 * @param {string} uid - The Cloudflare Stream video UID
 * @returns {Promise<Object>} - Playback URL information
 */
export async function getVideoPlaybackUrl(uid) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}/api/videos/${uid}/playback-url`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get playback URL');
  }

  return response.json();
}


/**
 * Update a lesson with the Cloudflare Stream video UID
 * 
 * @param {string} lessonId - The lesson ID
 * @param {string} uid - The Cloudflare Stream video UID
 * @returns {Promise<Object>} - Update result
 */
export async function updateLessonVideoUid(lessonId, uid) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}/api/lessons/${lessonId}/video-uid?uid=${encodeURIComponent(uid)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update lesson video');
  }

  return response.json();
}


/**
 * Check if Cloudflare Stream is configured
 * 
 * @returns {Promise<Object>} - Configuration status
 */
export async function checkStreamStatus() {
  try {
    const response = await fetch(`${API_URL}/api/videos/stream-status`);
    if (!response.ok) {
      return { configured: false };
    }
    return response.json();
  } catch (error) {
    return { configured: false };
  }
}


/**
 * Poll for video processing completion
 * 
 * @param {string} uid - The Cloudflare Stream video UID
 * @param {Object} options - Polling options
 * @param {number} options.interval - Polling interval in ms (default: 5000)
 * @param {number} options.timeout - Max polling time in ms (default: 300000 = 5 min)
 * @param {Function} options.onStatusChange - Callback when status changes
 * @returns {Promise<Object>} - Final video status when ready
 */
export async function waitForVideoReady(uid, options = {}) {
  const { 
    interval = 5000, 
    timeout = 300000,
    onStatusChange 
  } = options;
  
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    const status = await getVideoStatus(uid);
    
    if (onStatusChange) {
      onStatusChange(status);
    }
    
    if (status.ready_to_stream) {
      return status;
    }
    
    if (status.status === 'error') {
      throw new Error('Video processing failed');
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  
  throw new Error('Video processing timeout');
}
